import React, { useCallback, useEffect } from 'react';
import {
    ModalWrapper,
    Topbar,
    LogoWrapper,
    CloseWrapper,
    TopbarLayoutWrapper,
    ContentWrapper,
} from './Modal.styles';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Grid, GridCol, GridRow } from '@pegasus/ui-components/components/grid';
import { Closing } from '@pegasus/ui-components/components/icons/closing';
import { Color } from '@pegasus/ui-components/tokens/colors';
import { IconSize } from '@pegasus/ui-components/components/icons/types';
import Logo from '@client/components/shared/Logo';

interface ModalProps {
    onClose?: () => void;
    visible?: boolean;
    children: React.ReactNode;
}

const TRANSITION_TIMEOUT = 300;

const Modal = ({ onClose, visible = false, children }: ModalProps) => {
    const closeWithEscape = useCallback(
        (event: KeyboardEvent) => {
            if (event.keyCode === 27 && visible) {
                onClose();
            }
        },
        [onClose],
    );

    const onModalShow = useCallback(() => {
        window.addEventListener('keydown', closeWithEscape);
        document.body.classList.add('overflow--hidden');
    }, []);

    const onModalHide = useCallback(() => {
        document.body.classList.remove('overflow--hidden');
        window.removeEventListener('keydown', closeWithEscape);
    }, []);

    useEffect(() => {
        if (visible) {
            onModalShow();
        } else {
            onModalHide();
        }

        return onModalHide;
    }, [visible]);

    const handleClose = useCallback(() => {
        onModalHide();
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    return (
        <TransitionGroup>
            {visible && (
                <CSSTransition timeout={TRANSITION_TIMEOUT} classNames="item">
                    <ModalWrapper>
                        <Topbar>
                            <Grid>
                                <TopbarLayoutWrapper>
                                    <LogoWrapper>
                                        <Logo dark inline />
                                    </LogoWrapper>
                                    <CloseWrapper onClick={handleClose}>
                                        <Closing
                                            color={Color.dark}
                                            size={IconSize.XS}
                                        />
                                    </CloseWrapper>
                                </TopbarLayoutWrapper>
                            </Grid>
                        </Topbar>
                        <Grid>
                            <GridRow>
                                <GridCol
                                    span={12}
                                    spanSm={8}
                                    offsetSm={2}
                                    spanMd={6}
                                    offsetMd={3}
                                    spanLg={4}
                                    offsetLg={4}
                                >
                                    <ContentWrapper>{children}</ContentWrapper>
                                </GridCol>
                            </GridRow>
                        </Grid>
                    </ModalWrapper>
                </CSSTransition>
            )}
        </TransitionGroup>
    );
};

export default Modal;
